/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  circularProgressClasses,
  ClickAwayListener,
  Grid,
  IconButton,
  Link,
  OutlinedInput,
  Popover,
  Theme,
  Typography,
} from '@quillbot/design-system';
import StyledPremiumButton from '@Common/quillElements/button/PremiumButton';
import { GenericObject } from 'core/models/platform';
import {
  KEYS,
  ONE_KB,
  PROJECT_TITLE_REGEX,
  StorageLevelEnum,
} from 'src/researcher/constants/common.constants';
import { Tooltip } from 'src/researcher/component/common';
import NextLink from 'next/link';
import {
  fontSize,
  fontWeight,
  UNITS,
  w100p,
  unitPX,
  displayNone,
  displayFlex,
  grow,
  justifyStart,
  row,
  createBorder,
  createBorderRadius,
  pointerEventsNone,
  primaryColor,
  truncate,
  cursorPointer,
  overflowHidden,
  positionRelative,
  h100p,
  cursorDefault,
  center,
} from 'src/researcher/styles';
import {
  ArrowDownloadIcon,
  GridIcon,
  MoreVertIcon,
  OpenIcon,
  SearchIcon,
  TextBulletListLTRIcon,
  TextSortAscIcon,
  TrashIcon,
  TrashUnstripedIcon,
} from 'src/researcher/component/icons';
import { useRouter } from 'next/router';
import { useDebounceFn, useIsomorphicEffect, useLocalStorageState } from '@Common/hooks';
import { openToastr } from '@Common/handlers/services';
import { getCitationStyleFromLocalStorage } from 'src/citation-generator/utils';
import {
  ErrorDeletingProjectMsg,
  ErrorDownloadingProjectMsg,
  ExportButtonTooltip,
  GridViewTooltip,
  KebabMenuTooltip,
  ListViewTooltip,
  ProjectDeletedSuccessText,
  ProjectExportSuccessText,
  SearchProjectTooltip,
  SortByTooltip,
  TrashButtonTooltip,
} from 'src/researcher/constants/message.constants';
import {
  useApplicationDispatch,
  setTotalStorageUsedAction,
} from 'src/researcher/context/application';
import { deleteProjectAPI, restoreProjectAPI } from 'src/researcher/handlers/api';
import { convertToExportableContent } from 'src/researcher/utils/doc-export';
import {
  getDisplayDateAndTime,
  isString,
  replaceSpaces,
  storageFormatter,
  truncateStringAfter,
} from 'src/researcher/utils';
import { AsyncStatusEnum } from 'src/researcher/types/common';
import { IsVisible } from 'src/researcher/component/common/IsVisible';
import {
  useCoWriterStorageAllocation,
  useCoWriterUsedStorage,
} from 'src/researcher/hooks/useCowriterUserStorage';
import commonTrack from '@Common/handlers/track';
import { createDocFile } from '@Common/components/fileExporter/FileExporter';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import Track from 'core/logger/track';
import researcherTrack from 'src/researcher/handlers/track';
import { renderToString } from 'react-dom/server';
import { common_styles, pageStyles } from './styles.css';
import { useRecentTrashProjectState, useRecentTrashProjectDispatch } from './list.store';
import { CardIdProvider, KebabProvider, useCardId, useKebab } from './Card.provider';
import { useSliderVisibility } from './Slider';
import { CardLayout, CardInfo, card_controls_root_class } from './Card.layout';
import { DownloadFile } from './DownloadFile';
import {
  setDeletingProjectCardListStoreAction,
  deleteProjectFromListAction,
  setDownloadingProjectCardListStoreAction,
  startNewQuerySearchAction,
  SortByOrderBy,
  sortCurrentListAction,
} from './list.reducer';

export const async_loader_class = 'async_loader_container';
export const control_buttons_class = 'control_buttons';
export const card_view_grid_class = 'card_view_grid';
export const card_view_list_class = 'card_view_list';
export const card_info_container_class = 'card_info_container';

export const controlButtonStyles = { p: unitPX(UNITS[0]) };

export const loaderStyles = {
  width: unitPX(UNITS['3pt']),
  height: unitPX(UNITS['3pt']),
};

const styles = {
  btnSpacing: { marginLeft: unitPX(UNITS['1.5pt']) },
  btnP: {
    p: unitPX(UNITS['1px'] * 2),
  },
  btnIconSize: {
    width: unitPX(UNITS['2.5pt']),
    height: unitPX(UNITS['2.5pt']),
  },

  card: {
    boxSizing: 'border-box',
    [`& .${async_loader_class}`]: displayFlex,
    [`& .${control_buttons_class}`]: {
      ...displayNone,
      ...pointerEventsNone,
    },
  },

  borderAll: createBorder(['top', 'right', 'bottom', 'left'], { color: 'transparent' }),
  borderTop: createBorder(['top']),
  borderBottom: createBorder(['bottom']),
  cardListViewBorderHover: {
    '&:hover': {
      ...createBorderRadius(['topLeft', 'topRight', 'bottomRight', 'bottomLeft'], unitPX(UNITS.pt)),
      ...createBorder(['top', 'right', 'bottom', 'left'], primaryColor),
    },
  },

  kebabBorder: createBorder(['top', 'right', 'bottom', 'left']),
  kebabBorderRadius: createBorderRadius(
    ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'],
    unitPX(UNITS['1.5pt'])
  ),
  kebabInternalBorderRadius: createBorderRadius(
    ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'],
    unitPX(UNITS.pt)
  ),

  KebabButtonPadding: { px: unitPX(UNITS['1.5pt']) },

  kebabButton: {
    color: (theme: Theme) => theme.palette.text.primary,
    '&:hover': {
      backgroundColor: (theme: Theme) => theme.palette.background.default,
    },
  },
};

const opacity = {
  1: {
    opacity: 1,
  },
  0: {
    opacity: 0,
  },
};

/** #CARD */

function KebabButton() {
  const cardId = useCardId();
  const [anchorEl, setAnchorEl] = useKebab();
  const listProjectDispatch = useRecentTrashProjectDispatch();
  const applicationDispatch = useApplicationDispatch();
  const deleting = useRecentTrashProjectState((state) => state.recent.projects[cardId].deleting);
  const [o, setSliderVisible] = useSliderVisibility();

  const open = Boolean(anchorEl);
  const id = open ? `card-kebab-${cardId}` : undefined;

  const showHidePopover = (show: boolean) => {
    if (id) {
      const popover = document.getElementById(id);
      if (popover) {
        popover.style.display = show ? 'flex' : 'none';
      }
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    showHidePopover(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e: any) => {
    e.stopPropagation();
    showHidePopover(false);
    setAnchorEl(null);
  };

  const deleteProjectHandler = (e: any) => {
    e.stopPropagation();
    showHidePopover(false);
    listProjectDispatch(
      setDeletingProjectCardListStoreAction({ id: cardId, deleting: true, context: 'recent' })
    );
    if (!deleting) {
      researcherTrack.deletedProject('Temporary', 'Landing Page');
      deleteProjectAPI(cardId)
        .then((data) => {
          applicationDispatch(setTotalStorageUsedAction(data.totalStorageUsed));
          listProjectDispatch(deleteProjectFromListAction(cardId, 'recent', true));
          openToastr(
            ProjectDeletedSuccessText,
            4000,
            () => {
              setSliderVisible('trash-bin');
            },
            'Open Trash'
          );
        })
        .catch(() => {
          openToastr(ErrorDeletingProjectMsg);
          listProjectDispatch(
            setDeletingProjectCardListStoreAction({
              id: cardId,
              deleting: false,
              context: 'recent',
            })
          );
        })
        .finally(() => {});
    }
  };

  return (
    <Grid item component="span">
      <Tooltip title={KebabMenuTooltip} arrow placement="bottom">
        <span>
          <IconButton onClick={handleClick} sx={controlButtonStyles}>
            <MoreVertIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        elevation={2}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Grid
          container
          direction="column"
          sx={[styles.kebabBorder, styles.kebabInternalBorderRadius]}
        >
          <Button
            startIcon={<TrashUnstripedIcon />}
            onClick={deleteProjectHandler}
            sx={[
              fontSize[14],
              grow,
              styles.kebabButton,
              styles.KebabButtonPadding,
              displayFlex,
              row,
              justifyStart,
            ]}
          >
            Delete
          </Button>
          <Button
            startIcon={<OpenIcon />}
            onClick={(e) => {
              e.stopPropagation();
              researcherTrack.openedProjectNewTab();
              showHidePopover(false);
              setAnchorEl(null);
            }}
            sx={[
              fontSize[14],
              grow,
              styles.kebabButton,
              styles.KebabButtonPadding,
              displayFlex,
              row,
              justifyStart,
            ]}
          >
            <Link
              sx={{ color: 'inherit', textDecoration: 'none !important' }}
              href={`${window.location.href}/home?project=${cardId}`}
              target="_blank"
            >
              Open in new tab
            </Link>
          </Button>
        </Grid>
      </Popover>
    </Grid>
  );
}

function DownloadButton() {
  const id = useCardId();
  const { citedDataById } = useSelector((state: RootState) => state.ctgr);
  const listProjectDispatch = useRecentTrashProjectDispatch();
  const downloading = useRecentTrashProjectState((state) => state.recent.projects[id].downloading);

  const exportDocumentHandler = useCallback(async () => {
    listProjectDispatch(
      setDownloadingProjectCardListStoreAction({ id, downloading: true, context: 'recent' })
    );
    let data = '';
    if (!downloading) {
      try {
        const projectData = await restoreProjectAPI(id);
        const html = projectData.content;
        const projectName = projectData.title;
        const citations = projectData.citations;
        const citationStyleFromStorage = getCitationStyleFromLocalStorage();
        data = await convertToExportableContent(
          html,
          citations,
          citationStyleFromStorage,
          citedDataById
        );
        await createDocFile({ name: projectName, html: data });
        openToastr(ProjectExportSuccessText);
      } catch (err) {
        commonTrack.fileExported(false);
        console.log(err);
        openToastr(ErrorDownloadingProjectMsg);
      } finally {
        listProjectDispatch(
          setDownloadingProjectCardListStoreAction({ id, downloading: false, context: 'recent' })
        );
      }
    }
    return data;
  }, []);

  return (
    <Grid item component="span">
      <Tooltip title={ExportButtonTooltip} arrow placeholder="bottom">
        <span>
          <IconButton
            onClick={(event) => {
              // event.preventDefault();
              event.stopPropagation();
              researcherTrack.projectExport('Landing Page');
              exportDocumentHandler();
            }}
            sx={{
              padding: '0px !important',
              fontSize: `${unitPX(2)}`,
              height: `${unitPX(UNITS['2.5pt'])} !important`,
              width: `${unitPX(UNITS['2.5pt'])} !important`,
              // '&:hover': {
              //   background: 'rgba(0, 0, 0, 0.04)',
              //   color: theme.palette.primary.main,
              // },
            }}
          >
            <ArrowDownloadIcon
              // {...props}
              sx={{
                // '&:hover': primaryColor,
                width: unitPX(UNITS['2.5pt']),
                height: unitPX(UNITS['2.5pt']),
              }}
            />
          </IconButton>
        </span>
      </Tooltip>

      {/* <DownloadFile getText={exportDocumentHandler} projectName={projectName} /> */}
    </Grid>
  );
}

function CardControlButtonInList() {
  const id = useCardId();

  return (
    <>
      <Grid flex={1} container justifyContent="flex-end" className={card_controls_root_class}>
        <Grid item display="none" className={async_loader_class} pr={unitPX(UNITS.pt)}>
          <CircularProgress size="small" sx={loaderStyles} />
        </Grid>
        <Grid item className={control_buttons_class}>
          <DownloadButton />
          <KebabButton />
        </Grid>
      </Grid>
    </>
  );
}

function CardComponent() {
  const router = useRouter();

  const id = useCardId();
  const title = useRecentTrashProjectState((state) => state.recent.projects[id].title);
  const preview = useRecentTrashProjectState((state) => state.recent.projects[id].preview);
  const time = useRecentTrashProjectState((state) =>
    getDisplayDateAndTime(
      state.recent.projects[id].updatedAt || state.recent.projects[id].createdAt
    )
  );
  const controls = useRecentTrashProjectState((state) =>
    state.recent.projects[id].deleting || state.recent.projects[id].downloading
      ? 'card-mount'
      : 'hover'
  );

  const sx = useRecentTrashProjectState((state) =>
    state.recent.projects[id].deleting || state.recent.projects[id].downloading ? styles.card : null
  );

  return (
    <CardLayout
      sx={sx}
      controls={controls}
      onCardClick={() => {
        researcherTrack.selectedExistingProject();
        router.push(`/co-writer/home?project=${id}`);
      }}
    >
      <CardInfo
        title={truncateStringAfter(title, 19)}
        preview={preview}
        time={time}
        modifierClassName={card_info_container_class}
      >
        <CardControlButtonInList />
      </CardInfo>
    </CardLayout>
  );
}

const Card = memo(CardComponent);

function ListCardComponent() {
  const router = useRouter();
  const id = useCardId();
  const title = useRecentTrashProjectState((state) => state.recent.projects[id].title);
  const size = useRecentTrashProjectState((state) =>
    storageFormatter(Number(state.recent.projects[id].storageUsed) || 0)
  );
  const time = useRecentTrashProjectState((state) =>
    getDisplayDateAndTime(
      state.recent.projects[id].updatedAt || state.recent.projects[id].createdAt
    )
  );

  const sx = useRecentTrashProjectState((state) =>
    state.recent.projects[id].deleting || state.recent.projects[id].downloading ? styles.card : null
  );

  return (
    <Grid
      onClick={() => {
        researcherTrack.selectedExistingProject();
        router.push(`/co-writer/home?project=${id}`);
      }}
      xs={12}
      item
      py={unitPX(UNITS['3pt'])}
      px={unitPX(UNITS['1.5pt'])}
      sx={[sx, styles.borderAll, styles.cardListViewBorderHover, styles.borderTop, cursorPointer]}
    >
      <Grid container justifyContent="space-between">
        <Grid item xs={5}>
          <Typography
            sx={[fontSize[16], fontWeight[600], truncate, overflowHidden, { whiteSpace: 'nowrap' }]}
          >
            {truncateStringAfter(replaceSpaces(title?.trim?.() || ''), 35)}
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography sx={[fontSize[16], { color: '#686868' }]}>{size}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography sx={[fontSize[16], { color: '#686868' }]}>{time}</Typography>
        </Grid>
        <Grid item xs={1}>
          <Grid item display="none" className={async_loader_class} pr={unitPX(UNITS.pt)}>
            <CircularProgress size="small" sx={loaderStyles} />
          </Grid>
          <Grid item className={control_buttons_class}>
            <DownloadButton />
            <KebabButton />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

const ListCard = memo(ListCardComponent);

/** #CARD */

function NoProjectsFound({ pt, mt = '0px' }: { pt: string; mt?: string }) {
  const query = useRecentTrashProjectState((state) => replaceSpaces(state.recent.query).trim());
  const isLoading = useRecentTrashProjectState(
    (state) => state.recent.status === AsyncStatusEnum.PENDING
  );

  if (isLoading) {
    return null;
  }

  return (
    <Grid
      item
      xs={12}
      style={{ paddingTop: pt }}
      px={unitPX(UNITS['1.5pt'])}
      mt={mt}
      sx={[displayFlex, center]}
    >
      <Typography sx={[fontSize[16], fontWeight[600], { color: '#686868' }]}>
        {query.length !== 0 && 'No projects were found matching your search query.'}
      </Typography>
    </Grid>
  );
}

// eslint-disable-next-line react/display-name
const GridView = memo((props: { projectIds: string[] }) =>
  props.projectIds.length > 0 ? (
    <>
      {props.projectIds.map((id) => (
        <CardIdProvider key={id} id={id}>
          <KebabProvider>
            <Card />
          </KebabProvider>
        </CardIdProvider>
      ))}
    </>
  ) : (
    <>
      <NoProjectsFound pt={unitPX(UNITS['14pt'])} mt={unitPX(UNITS['2.5pt'])} />
    </>
  )
);

// eslint-disable-next-line react/display-name
const ListView = memo((props: { projectIds: string[] }) =>
  props.projectIds.length > 0 ? (
    <>
      <Grid item xs={12} pb={unitPX(UNITS['3pt'])} px={unitPX(UNITS['1.5pt'])}>
        <Grid container justifyContent="space-between">
          <Grid item xs={5}>
            <Typography sx={[fontSize[16], fontWeight[600], { color: '#686868' }]}>
              Title
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography sx={[fontSize[16], fontWeight[600], { color: '#686868' }]}>Size</Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography sx={[fontSize[16], fontWeight[600], { color: '#686868' }]}>Date</Typography>
          </Grid>
          <Grid item xs={1} />
        </Grid>
      </Grid>
      {props.projectIds.map((id) => (
        <CardIdProvider key={id} id={id}>
          <KebabProvider>
            <ListCard />
          </KebabProvider>
        </CardIdProvider>
      ))}
    </>
  ) : (
    <>
      <NoProjectsFound pt={unitPX(UNITS['14pt'])} />
    </>
  )
);

function IsGridView({ view, ...props }: React.PropsWithChildren<{ view: 'grid' | 'list' }>) {
  return (
    <IsVisible
      visible={view === 'grid'}
      Component={Grid}
      container
      item
      xs={12}
      flex={1}
      sx={[common_styles.contentPadding, { px: 0 }]}
      rowSpacing={14}
      columnSpacing={{ sm: 24, md: 28, lg: 34 }}
      {...props}
    />
  );
}

function IsListView({ view, ...props }: React.PropsWithChildren<{ view: 'grid' | 'list' }>) {
  return (
    <IsVisible
      visible={view === 'list'}
      Component={Grid}
      container
      item
      xs={12}
      flex={1}
      sx={[common_styles.contentPadding, { px: 0 }]}
      {...props}
    />
  );
}

type SortByOrderByItem = { label: string; value: SortByOrderBy };
type SortByOrderByItemArr = SortByOrderByItem[];
const SortByOrderByItems: SortByOrderByItemArr = [
  { label: 'Sort by date modified', value: 'date:desc' },
  { label: 'Sort by name', value: 'name:asc' },
  { label: 'Sort by size', value: 'size:desc' },
];

function SortMenuButton() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'sort-live-project' : undefined;
  const currentSort = useRecentTrashProjectState((state) => state.recent.sort);
  const dispatch = useRecentTrashProjectDispatch();

  const showHidePopover = (show: boolean) => {
    if (id) {
      const popover = document.getElementById(id);
      if (popover) {
        popover.style.display = show ? 'flex' : 'none';
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    showHidePopover(false);
  };

  const onChange = (nextSort: SortByOrderBy) => {
    if (currentSort !== nextSort) {
      researcherTrack.sortedFor(nextSort);
      dispatch(sortCurrentListAction(nextSort, 'recent'));
      handleClose();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Grid item>
        <Tooltip arrow title={SortByTooltip}>
          <span style={styles.btnSpacing}>
            <IconButton sx={[styles.btnP]} onClick={(e: any) => setAnchorEl(e.currentTarget)}>
              <TextSortAscIcon sx={styles.btnIconSize} />
            </IconButton>
          </span>
        </Tooltip>
        <Popover
          elevation={2}
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Grid
            container
            direction="column"
            sx={[
              styles.kebabBorder,
              styles.kebabInternalBorderRadius,
              { width: unitPX(UNITS['22pt']) },
            ]}
          >
            {SortByOrderByItems.map(({ label, value }) => (
              <Button
                key={value}
                onClick={() => currentSort !== value && onChange(value)}
                sx={[
                  fontSize[14],
                  grow,
                  styles.kebabButton,
                  styles.KebabButtonPadding,
                  displayFlex,
                  row,
                  justifyStart,
                  { py: unitPX(UNITS['0.5pt']) },
                  currentSort === value && primaryColor,
                  currentSort === value && cursorDefault,
                ]}
              >
                {label}
              </Button>
            ))}
          </Grid>
        </Popover>
      </Grid>
    </ClickAwayListener>
  );
}

const getStorageStatusColor = (storagePercent: number, theme: Theme) => {
  let ret = theme.palette.primary.main;

  if (storagePercent > 90) {
    ret = theme.palette.error.main;
  } else if (storagePercent > 80) {
    ret = theme.specialColors.WARNING_BAR;
  }

  return ret;
};

function StorageProgress() {
  const { storageLevel, totalStorageUsedPercent, totalStorageUsed } = useCoWriterUsedStorage();
  const { TOTAL_STORAGE_ALLOCATED } = useCoWriterStorageAllocation();

  const storageStatusTooltipText = `${
    // if storage is below 0 i.e negative means hea has exahusted storage
    // eslint-disable-next-line no-nested-ternary
    totalStorageUsed >= 100 * ONE_KB
      ? storageFormatter(
          TOTAL_STORAGE_ALLOCATED - totalStorageUsed < 0
            ? 0
            : TOTAL_STORAGE_ALLOCATED - totalStorageUsed
        )
      : storageFormatter(totalStorageUsed)
  } ${totalStorageUsed >= 100 * ONE_KB ? 'left of' : 'used of'} ${storageFormatter(
    TOTAL_STORAGE_ALLOCATED
  )}`;

  const shouldShowStorage = totalStorageUsedPercent > 80;

  return (
    <Grid item sx={[shouldShowStorage ? displayFlex : displayNone, { ml: unitPX(UNITS.pt) }]}>
      <Tooltip arrow title={storageStatusTooltipText}>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={[positionRelative, w100p, h100p]}
        >
          <CircularProgress
            variant="determinate"
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
            size={18}
            thickness={5}
            value={100}
          />
          <CircularProgress
            variant="determinate"
            value={
              TOTAL_STORAGE_ALLOCATED - totalStorageUsed < 0
                ? 100
                : (totalStorageUsed / TOTAL_STORAGE_ALLOCATED) * 100
            }
            disableShrink
            sx={{
              animationDuration: '550ms',
              position: 'absolute',
              left: 0,
              [`& .${circularProgressClasses.circle}`]: {
                strokeLinecap: 'round',
              },
              color: (theme) => getStorageStatusColor(totalStorageUsedPercent, theme),
            }}
            size={18}
            thickness={5}
          />
        </Grid>
      </Tooltip>
    </Grid>
  );
}

function SearchBox() {
  const [focus, setFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useRecentTrashProjectDispatch();

  const searchNew = (query: string) => {
    if (isString(query)) {
      dispatch(startNewQuerySearchAction(query, 'recent'));
    }
    researcherTrack.searchProject();
  };
  const debouncedSearchNew = useDebounceFn(searchNew);

  const onClick = () => searchNew(inputRef.current?.value || '');
  const onChange = (e: any) => debouncedSearchNew(e.target.value);

  useIsomorphicEffect(() => {
    if (focus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focus]);

  useEffect(() => {
    /** Make sure the search is with safe query and don't allow to enter anything other then valid char */
    /** https://stackoverflow.com/a/61697867/7579856  */
    const ele = inputRef.current;
    const state: GenericObject = {
      value: ele?.value,
    };
    const onKeyDown = (e: any) => {
      const target = e.target;
      state.selectionStart = target.selectionStart;
      state.selectionEnd = target.selectionEnd;
    };
    const onInput = (e: any) => {
      const target = e.target;

      if (PROJECT_TITLE_REGEX.test(target.value)) {
        state.value = target.value;
      } else {
        // Users enter the not supported characters
        // Restore the value and selection
        target.value = state.value;
        target.setSelectionRange(state.selectionStart, state.selectionEnd);
      }
    };
    if (ele) {
      ele.addEventListener('keydown', onKeyDown);

      ele.addEventListener('input', onInput);
    }

    return () => {
      if (ele) {
        ele.removeEventListener('keydown', onKeyDown);
        ele.removeEventListener('input', onInput);
      }
    };
  }, []);

  return (
    <ClickAwayListener onClickAway={() => !inputRef.current?.value && setFocus(false)}>
      <Grid item>
        <Grid
          container
          item
          sx={[focus ? displayFlex : displayNone]}
          style={focus ? opacity[1] : opacity[0]}
        >
          <OutlinedInput
            onChange={onChange}
            inputProps={{
              ref: inputRef,
            }}
            startAdornment={
              <SearchIcon sx={[styles.btnIconSize, cursorPointer]} onClick={onClick} />
            }
            sx={[
              {
                '& input.MuiInputBase-input': { p: unitPX(UNITS['0.5pt']), ...fontSize[14] },
                '&.MuiInputBase-adornedStart': { pl: unitPX(UNITS['0.5pt']) },
              },
            ]}
          />
        </Grid>
        <Grid item sx={[focus ? displayNone : displayFlex]}>
          <Tooltip arrow title={SearchProjectTooltip}>
            <span style={styles.btnSpacing}>
              <IconButton
                style={focus ? opacity[0] : opacity[1]}
                sx={[styles.btnP]}
                onClick={() => setFocus(true)}
              >
                <SearchIcon sx={styles.btnIconSize} />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </ClickAwayListener>
  );
}

function TrashBinButton() {
  const [o, setSliderVisible] = useSliderVisibility();
  return (
    <Tooltip arrow title={TrashButtonTooltip}>
      <span style={styles.btnSpacing}>
        <IconButton
          sx={[styles.btnP]}
          onClick={() => {
            researcherTrack.openedTrashView();
            setSliderVisible('trash-bin');
          }}
        >
          <TrashUnstripedIcon sx={styles.btnIconSize} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

function Loader() {
  const isLoading = useRecentTrashProjectState(
    (state) => state.recent.status === AsyncStatusEnum.PENDING
  );

  const hasNoProjects = useRecentTrashProjectState((state) => state.recent.count === 0);

  return (
    <Grid container item xs={12} justifyContent="center" alignItems="center">
      <IsVisible
        visible={isLoading}
        sx={[{ pt: unitPX(hasNoProjects ? UNITS['10pt'] : UNITS['2pt']) }]}
      >
        <CircularProgress
          size="small"
          sx={{ width: unitPX(UNITS['3pt']), height: unitPX(UNITS['3pt']) }}
        />
      </IsVisible>
    </Grid>
  );
}

function StorageBanner() {
  const { totalStorageUsed, totalStorageUsedPercent, storageLevel } = useCoWriterUsedStorage();
  const { TOTAL_STORAGE_ALLOCATED, TOTAL_STORAGE_ALLOCATED_PREMIUM, premium } =
    useCoWriterStorageAllocation();

  let messages: any[] = [];

  const showStorageBanner = storageLevel !== StorageLevelEnum.NORMAL;

  const UpsellPremium = renderToString(
    <span>
      <NextLink href="/premium?referrer=cowriterStorage">
        <a
          role="button"
          onKeyUp={() => {}}
          tabIndex={0}
          onClick={() => Track.getInstance().premiumLinkClicked('cowriterStorage')}
          target="_blank"
        >
          Get Premium
        </a>
      </NextLink>{' '}
      to upgrade to {storageFormatter(TOTAL_STORAGE_ALLOCATED_PREMIUM)}
    </span>
  );

  if (storageLevel === StorageLevelEnum.INFO) {
    messages = [
      `You are reaching storage limit. Please, delete some projects to free some space${
        premium ? '.' : ''
      }`,
    ];
  }

  if (storageLevel === StorageLevelEnum.WARNING) {
    messages = [`Storage is almost full. Please, delete some projects${premium ? '.' : ''}`];
  }

  if (storageLevel === StorageLevelEnum.DANGER || storageLevel === StorageLevelEnum.CRITICAL) {
    messages = [];
    messages = [`Storage is almost full. Please, delete some projects${premium ? '.' : ''}`];
  }

  if (!premium) {
    messages.push('or');
    messages.push(UpsellPremium);
  }

  return (
    <IsVisible
      visible={showStorageBanner}
      Component={Grid}
      container
      item
      p={unitPX(UNITS['2pt'])}
      mt={unitPX(UNITS['3pt'])}
      mb={unitPX(UNITS.pt)}
      justifyContent="space-between"
      alignItems="center"
      sx={[
        { boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.15)' },
        createBorderRadius(
          ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'],
          unitPX(UNITS['1.5pt'])
        ),
      ]}
    >
      <Typography sx={[h100p, fontSize[14], fontWeight[600]]}>
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: messages.join('&nbsp;') }} />
      </Typography>
      {!premium && (
        <StyledPremiumButton
          style={{
            fontWeight: 600,
            fontSize: 16,
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 2,
            paddingBottom: 2,
          }}
          referrer="homePageCovertUsers"
        />
      )}
    </IsVisible>
  );
}

function ListProjectsComponent() {
  const projectsCollection = useRecentTrashProjectState((state) => state.recent.projects);
  const projectIds = useMemo(() => Object.keys(projectsCollection), [projectsCollection]);
  const [view, setView] = useLocalStorageState<'grid' | 'list'>(
    KEYS.LOCAL_STORAGE_LIST_GRID_VIEW,
    'grid'
  );
  return (
    <Grid item component="section" sx={[w100p, pageStyles.maxPageWidth]}>
      <Grid container direction="column">
        <Grid container item xs={12} justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography component="h3" sx={[fontSize[20], fontWeight[400]]}>
              Recent Projects
            </Typography>
          </Grid>
          <Grid item flex={1}>
            <Grid container justifyContent="flex-end">
              <SearchBox />
              {/* storage circular bar */}
              <StorageProgress />
              <Tooltip arrow title={view === 'grid' ? ListViewTooltip : GridViewTooltip}>
                <span style={styles.btnSpacing}>
                  <IconButton
                    sx={[styles.btnP]}
                    onClick={() => {
                      researcherTrack.switchView(view);
                      setView((v) => (v === 'grid' ? 'list' : 'grid'));
                    }}
                  >
                    {view === 'grid' ? (
                      <TextBulletListLTRIcon sx={styles.btnIconSize} />
                    ) : (
                      <GridIcon sx={styles.btnIconSize} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <SortMenuButton />
              <TrashBinButton />
            </Grid>
          </Grid>
        </Grid>
        {/* storage banner */}
        {/* <StorageBanner /> */}

        {/* renders required view making other display-none */}
        <IsGridView view={view}>
          <GridView projectIds={projectIds} />
        </IsGridView>
        <IsListView view={view}>
          <ListView projectIds={projectIds} />
        </IsListView>
        <Loader />
      </Grid>
    </Grid>
  );
}

export const ListProjects = memo(ListProjectsComponent);
